import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, unref as _unref, createVNode as _createVNode, createElementVNode as _createElementVNode, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { class: "upload-panel" }
const _hoisted_2 = {
  key: 0,
  class: "upload-panel__intro"
}
const _hoisted_3 = { class: "upload-panel__message upload-panel__message--primary" }
const _hoisted_4 = { class: "upload-panel__divider" }
const _hoisted_5 = { class: "upload-panel__file-select" }
const _hoisted_6 = { class: "upload-panel__file-select-text" }
const _hoisted_7 = ["accept"]
const _hoisted_8 = { class: "upload-panel__hint" }
const _hoisted_9 = {
  key: 1,
  class: "upload-panel__file-info"
}
const _hoisted_10 = { class: "upload-panel__file-name" }
const _hoisted_11 = { class: "upload-panel__file-size" }
const _hoisted_12 = {
  key: 2,
  class: "upload-panel__placeholder"
}
const _hoisted_13 = {
  key: 3,
  class: "upload-panel__error"
}
const _hoisted_14 = { class: "upload-panel__actions" }
const _hoisted_15 = ["disabled"]
const _hoisted_16 = ["disabled"]

import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import Mdi from '@/corePagesModule/components/Mdi.vue';
import { i18n } from '@/i18n';

/**
 * アップロードパネルが発火するイベント
 */
type Emits = {
  /** ファイルが選択された */
  (event: 'file-selected', file: File): void;
  /** 選択済みファイルを破棄する */
  (event: 'cancel'): void;
  /** 書式読み込みアクションを実行する */
  (event: 'submit', file: File): void;
  /** 許可されていないファイルが選択された */
  (event: 'invalid-file', reason: string): void;
};

/**
 * アップロードパネルの入力パラメータ
 */
type Props = {
  /** 書式読込ボタンのロード状態 */
  loading?: boolean;
  /** パネル状態の外部リセットトリガー */
  resetKey?: number;
  /** 選択可能な拡張子 */
  acceptExtensions?: string[];
  /** ドロップゾーン上部に表示する説明 */
  introMessage?: string;
};


export default /*@__PURE__*/_defineComponent({
  __name: 'OrderTemplateUploadPanel',
  props: {
    loading: { type: Boolean, default: false },
    resetKey: { default: 0 },
    acceptExtensions: { default: () => ['.xlsx'] },
    introMessage: { default: '' }
  },
  emits: ["file-selected", "cancel", "submit", "invalid-file"],
  setup(__props: any, { emit: __emit }) {

const t = i18n.t;
const props = __props;

const emits = __emit;

const fileInput = ref<HTMLInputElement | null>(null);
const selectedFile = ref<File | null>(null);
const isDragging = ref(false);
const errorMessage = ref('');

/**
 * ファイル入力のaccept属性値を返す
 * @returns - accept属性値
 */
const acceptAttribute = computed(() => props.acceptExtensions.join(','));

/**
 * ファイルサイズ表示を計算する
 */
const formattedFileSize = computed(() => {
  if (!selectedFile.value) {
    return '';
  }
  const size = selectedFile.value.size;
  if (size < 1024) {
    return `${size} B`;
  }
  if (size < 1024 * 1024) {
    return `${(size / 1024).toFixed(1)} KB`;
  }
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
});

/**
 * ドラッグ関連イベントの既定動作を抑止する
 * @param event - ブラウザイベント
 */
const preventDefaults = (event: DragEvent) => {
  event.preventDefault();
  event.stopPropagation();
};

/**
 * ドラッグ状態の開始を処理する
 * @param event - ブラウザイベント
 */
const handleDragEnter = (event: DragEvent) => {
  preventDefaults(event);
  isDragging.value = true;
};

/**
 * ドラッグ状態の終了を処理する
 * @param event - ブラウザイベント
 */
const handleDragLeave = (event: DragEvent) => {
  preventDefaults(event);
  isDragging.value = false;
};

/**
 * ドラッグ＆ドロップされたファイルを処理する
 * @param event - ブラウザイベント
 */
const handleDrop = (event: DragEvent) => {
  preventDefaults(event);
  isDragging.value = false;
  const droppedFiles = event.dataTransfer?.files;
  if (!droppedFiles || droppedFiles.length === 0) {
    return;
  }
  handleFiles(droppedFiles[0]);
};

/**
 * ファイル選択ボタン経由で選択されたファイルを処理する
 * @param event - ブラウザイベント
 */
const handleChange = (event: Event) => {
  const target = event.target as HTMLInputElement | null;
  const file = target?.files?.[0];
  if (file) {
    handleFiles(file);
  }
};

/**
 * 選択されたファイルを検証する
 * @param file - ユーザーが指定したファイル
 */
const handleFiles = (file: File) => {
  const extension = `.${file.name.split('.').pop()?.toLowerCase() ?? ''}`;
  if (!props.acceptExtensions.includes(extension)) {
    const message = t('対応していない拡張子です') as string;
    errorMessage.value = message;
    emits('invalid-file', message);
    return;
  }
  errorMessage.value = '';
  selectedFile.value = file;
  emits('file-selected', file);
};

/**
 * ファイル選択をキャンセルする
 */
const handleCancel = () => {
  selectedFile.value = null;
  errorMessage.value = '';
  if (fileInput.value) {
    fileInput.value.value = '';
  }
  emits('cancel');
};

/**
 * ファイル送信を実行する
 */
const handleSubmit = () => {
  if (!selectedFile.value || props.loading) {
    return;
  }
  emits('submit', selectedFile.value);
};

watch(
  () => props.resetKey,
  () => {
    handleCancel();
  }
);

onMounted(() => {
  window.addEventListener('dragover', preventDefaults);
  window.addEventListener('drop', preventDefaults);
});

onBeforeUnmount(() => {
  window.removeEventListener('dragover', preventDefaults);
  window.removeEventListener('drop', preventDefaults);
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (props.introMessage)
      ? (_openBlock(), _createElementBlock("p", _hoisted_2, _toDisplayString(props.introMessage), 1))
      : _createCommentVNode("", true),
    _createElementVNode("div", {
      class: _normalizeClass(["upload-panel__drop-zone", { 'upload-panel__drop-zone--active': isDragging.value }]),
      onDragenter: handleDragEnter,
      onDragover: handleDragEnter,
      onDragleave: handleDragLeave,
      onDrop: handleDrop
    }, [
      _createVNode(Mdi, {
        class: "upload-panel__icon",
        icon: "file_download",
        "aria-label": _unref(t)('ファイルを読み込み')
      }, null, 8, ["aria-label"]),
      _createElementVNode("p", _hoisted_3, _toDisplayString(_unref(t)('ここにファイルをドロップ')), 1),
      _createElementVNode("p", _hoisted_4, _toDisplayString(_unref(t)('または')), 1),
      _createElementVNode("label", _hoisted_5, [
        _createElementVNode("span", _hoisted_6, _toDisplayString(_unref(t)('ファイルを選択')), 1),
        _createElementVNode("input", {
          ref_key: "fileInput",
          ref: fileInput,
          class: "upload-panel__input",
          type: "file",
          accept: acceptAttribute.value,
          onChange: handleChange
        }, null, 40, _hoisted_7)
      ]),
      _createElementVNode("p", _hoisted_8, _toDisplayString(_unref(t)('対応形式')) + ": " + _toDisplayString(props.acceptExtensions.join(', ')), 1)
    ], 34),
    (selectedFile.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
          _createElementVNode("span", _hoisted_10, _toDisplayString(selectedFile.value.name), 1),
          _createElementVNode("span", _hoisted_11, _toDisplayString(formattedFileSize.value), 1)
        ]))
      : (_openBlock(), _createElementBlock("p", _hoisted_12, _toDisplayString(_unref(t)('ファイルが選択されていません')), 1)),
    (errorMessage.value)
      ? (_openBlock(), _createElementBlock("p", _hoisted_13, _toDisplayString(errorMessage.value), 1))
      : _createCommentVNode("", true),
    _createElementVNode("div", _hoisted_14, [
      _createElementVNode("button", {
        class: "upload-panel__button upload-panel__button--primary",
        disabled: !selectedFile.value || _ctx.loading,
        onClick: handleSubmit
      }, _toDisplayString(_unref(t)('書式読み込み')), 9, _hoisted_15),
      _createElementVNode("button", {
        class: "upload-panel__button upload-panel__button--secondary",
        disabled: !selectedFile.value || _ctx.loading,
        onClick: handleCancel
      }, _toDisplayString(_unref(t)('キャンセル')), 9, _hoisted_16)
    ])
  ]))
}
}

})